import './FansSortModal.sass';

import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import format from 'date-fns/format';
import { fromUnixTime } from 'date-fns';

import { Button, Header, List, ListItem, ModalWrapper, ScrollView } from '$uikit';
import { BottomSheet } from '$uikit/BottomSheet/BottomSheet';
import { IcCheck16 } from '$assets';
import { useTranslator } from '$hooks';
import { promoLinksActions, promoLinksSelector } from '$store/promoLinks';

import { FanOrder, FanSort, FansLinksFilter } from '../StatisticsProFans.types';
import { DateRangePreset, PRESET_DEFAULTS, PRESET_KEYS } from './DateRangeSection';
import { DateRangeSheet } from './DateRangeSheet';
import { FansLinksSheet } from './FansLinksSheet';

const formatDay = (unix: number) => format(fromUnixTime(unix), 'd MMM');

interface Choice<V extends string> {
  value: V;
  key: string;
  fallback: string;
}

const SORT_OPTIONS: Choice<FanSort>[] = [
  { value: 'amount', key: 'statistics_pro_fans_sort_amount', fallback: 'By amount' },
  { value: 'count', key: 'statistics_pro_fans_sort_count', fallback: 'By frequency' },
];

const ORDER_OPTIONS: Choice<FanOrder>[] = [
  { value: 'desc', key: 'statistics_pro_fans_order_desc', fallback: 'Highest first' },
  { value: 'asc', key: 'statistics_pro_fans_order_asc', fallback: 'Lowest first' },
];

const labelOf = <V extends string>(
  t: ReturnType<typeof useTranslator>,
  options: Choice<V>[],
  value: V,
): string => {
  const found = options.find((o) => o.value === value);
  return found ? (t(found.key, { defaultValue: found.fallback }) as string) : '';
};

function OptionSheet<V extends string>({
  title,
  options,
  value,
  onPick,
  onClose,
}: {
  title: string;
  options: Choice<V>[];
  value: V;
  onPick: (value: V) => void;
  onClose: () => void;
}) {
  const t = useTranslator();
  return (
    <BottomSheet title={title} onClose={onClose} className="ActionBottomSheet">
      <div className="ActionBottomSheet__content">
        <List>
          {options.map((opt) => (
            <ListItem
              key={opt.value}
              indicator={value === opt.value ? <IcCheck16 /> : null}
              onClick={() => {
                onPick(opt.value);
                onClose();
              }}
            >
              {t(opt.key, { defaultValue: opt.fallback })}
            </ListItem>
          ))}
        </List>
      </div>
    </BottomSheet>
  );
}

export interface FansSortModalProps {
  sort: FanSort;
  order: FanOrder;
  preset: DateRangePreset;
  startDay: number;
  endDay: number;
  links: FansLinksFilter;
  onChangeSort: (sort: FanSort) => void;
  onChangeOrder: (order: FanOrder) => void;
  onChangeRange: (preset: DateRangePreset, startDay: number, endDay: number) => void;
  onChangeLinks: (links: FansLinksFilter) => void;
  onClose: () => void;
}

export const FansSortModal = ({
  sort,
  order,
  preset,
  startDay,
  endDay,
  links,
  onChangeSort,
  onChangeOrder,
  onChangeRange,
  onChangeLinks,
  onClose,
}: FansSortModalProps) => {
  const t = useTranslator();
  const dispatch = useDispatch();
  const { links: promoLinks } = useSelector(promoLinksSelector);
  const [sheet, setSheet] = useState<'sort' | 'order' | 'period' | 'links' | null>(null);

  useEffect(() => {
    dispatch(promoLinksActions.loadLinks());
  }, [dispatch]);

  const sortTitle = t('statistics_pro_fans_sort_title', { defaultValue: 'Sort by' });
  const orderTitle = t('statistics_pro_fans_order_title', { defaultValue: 'Order' });
  const periodTitle = t('statistics_pro_period', { defaultValue: 'Period' });
  const linksTitle = t('statistics_pro_links_label', { defaultValue: 'Links' });

  const linksLabel = (() => {
    if (links === 'all') {
      return t('statistics_pro_links_all', { defaultValue: 'All' });
    }
    if (!links.length) {
      return '';
    }
    const first = promoLinks.find((l) => l.id === links[0])?.name ?? '';
    return links.length > 1 ? `${first} +${links.length - 1}` : first;
  })();

  const dateRangeLabel =
    preset === 'custom'
      ? `${formatDay(startDay)} — ${formatDay(endDay)}`
      : t(PRESET_KEYS[preset], { defaultValue: PRESET_DEFAULTS[preset] });

  return (
    <ModalWrapper name="statistics_pro_fans_sort" onOverlayClick={onClose}>
      <Header
        showCloseButton
        backButtonOnClick={onClose}
        rightContent={
          <div className="Header__right FansSortModal__apply">
            <Button mode="inline" size="extra-small" onClick={onClose}>
              {t('statistics_pro_apply', { defaultValue: 'Apply' })}
            </Button>
          </div>
        }
      >
        {t('statistics_pro_view_options', { defaultValue: 'View options' })}
      </Header>
      <ScrollView className="FansSortModal">
        <List>
          <ListItem chevron indicator={dateRangeLabel} onClick={() => setSheet('period')}>
            {periodTitle}
          </ListItem>
          <ListItem
            chevron
            indicator={labelOf(t, SORT_OPTIONS, sort)}
            onClick={() => setSheet('sort')}
          >
            {sortTitle}
          </ListItem>
          <ListItem
            chevron
            indicator={labelOf(t, ORDER_OPTIONS, order)}
            onClick={() => setSheet('order')}
          >
            {orderTitle}
          </ListItem>
          {(promoLinks.length > 0 || links === 'all' || links.length > 0) && (
            <ListItem chevron indicator={linksLabel} onClick={() => setSheet('links')}>
              {linksTitle}
            </ListItem>
          )}
        </List>
      </ScrollView>

      {sheet === 'sort' && (
        <OptionSheet
          title={sortTitle}
          options={SORT_OPTIONS}
          value={sort}
          onPick={onChangeSort}
          onClose={() => setSheet(null)}
        />
      )}

      {sheet === 'order' && (
        <OptionSheet
          title={orderTitle}
          options={ORDER_OPTIONS}
          value={order}
          onPick={onChangeOrder}
          onClose={() => setSheet(null)}
        />
      )}

      {sheet === 'period' && (
        <DateRangeSheet
          preset={preset}
          startDay={startDay}
          endDay={endDay}
          onChange={onChangeRange}
          onClose={() => setSheet(null)}
        />
      )}

      {sheet === 'links' && (
        <FansLinksSheet
          value={links}
          onApply={onChangeLinks}
          onClose={() => setSheet(null)}
        />
      )}
    </ModalWrapper>
  );
};
